import type { SanityHomepageContentCards } from '#/lib/sanity/queries/homepageContentCards';
import {
  getSectionThemeAccent,
  getSectionThemeSurface,
  normalizeSectionTheme,
} from '#/lib/section-theme';
import { cn } from '#/lib/utils';
import { Link } from '#/components/Link';

import { Button } from './Button';
import { formatHeadline } from './Headline';
import { Media } from './Media';

export function HomepageContentCards(props: SanityHomepageContentCards) {
  const theme = normalizeSectionTheme(props.theme, 'purple');
  const accent = getSectionThemeAccent(theme);
  const sectionClass =
    theme === 'purple' && props.background === 'gradient'
      ? 'bg-cipher-charcoal-gradient text-cloud'
      : getSectionThemeSurface(theme);

  return (
    <div className={cn('p-main py-40 m:py-80 l:py-100', sectionClass)}>
      <div className="fade-in mx-auto flex max-w-1235 flex-col gap-24 m:gap-48">
        <div className="flex flex-col gap-24 m:gap-48">
          {props.eyebrow ? (
            <p className={cn('type-eyebrow', accent)}>{props.eyebrow}</p>
          ) : null}
          <h2 className="type-heading5 m:type-heading3 max-w-[800px] text-balance">
            {formatHeadline(props.headline, accent)}
          </h2>
          {props.link?.href ? (
            <Button
              href={props.link.href}
              target={props.link.target}
              variant="link"
              className="block w-full text-current m:w-auto"
            >
              {props.link.title}
            </Button>
          ) : null}
        </div>
        <div className="grid grid-cols-1 gap-24 m:grid-cols-2 l:grid-cols-3">
          {props.items.map((item) => (
            <Link
              key={item._key}
              href={item.href}
              className="flex h-full flex-col overflow-hidden rounded-16 text-charcoal"
            >
              <Media
                media={item.image}
                className="aspect-[2/1] w-full rounded-none"
                mediaClassName="rounded-none"
                aspect={false}
                sizes={{ m: '50vw', l: '33vw', '1285px': '412px' }}
              />
              <div className="flex flex-1 flex-col bg-cloud p-20 m:p-40">
                <h3 className="type-heading5 text-18 m:text-24">
                  {item.title}
                </h3>
                <p className="type-paragraph-sm m:type-paragraph mt-20">
                  {item.excerpt}
                </p>
                <Button variant="link" className="mt-auto pt-20 m:pt-40" asDiv>
                  {item.buttonText || 'Read the case study'}
                </Button>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
